import { useState, useRef, useEffect } from "react";
import { useSelector } from 'react-redux';
import useOutsideClick from 'Hooks/useOutsideClick';
import { IRootState } from 'Scripts/store';
import useDebounce from 'Hooks/useDebounce';
import Loader from 'Components/Loader';
import ProductSearchResult from './ProductSearchResult';
import {type SearchResult} from './ProductSearchResult';
import { useProductSearchMutation } from 'Services/productSearchApi';
import {type SearchProduct, type SearchVariant} from 'Services/productSearchApi';
import {type CartVariantData} from 'Components/CustomerServiceCart';
import MagnicifactionGlassIcon from 'Icons/magnificationGlass.svg?react';

const ProductSearch = ({
    addToCart
}: {
    addToCart: (
        id: string,
        variantData: any,
    ) => void;
}) => {
    const { translations } =
        useSelector((state: IRootState) => state?.cart?.miniCartModel);
    const [showDropdown, toggleDropdown] = useState(false);
    const impersonationSelectRef = useRef<HTMLInputElement>(null);
    const [productSearchQuery, setProductSearchQuery] = useState('');
    const [prevSearchQuery, setPrevSearchQuery] = useState('');
    const [showLoading, setShowLoading] = useState(false);
    const [searchResultList, setSearchResultList] = useState<SearchResult[]>([]);
    const [
        getProductSearchResult,
        { data: productSearchResult, isSuccess: searchSuccess, isLoading: searchLoading, isError: searchError },
    ] = useProductSearchMutation();

    const debouncedSearch = useDebounce(productSearchQuery, 1000);
    const impersonationDropdownRef = useOutsideClick(
        () => toggleDropdown(false),
        impersonationSelectRef
    );

    useEffect(() => {
        if (debouncedSearch) {
            getProductSearchResult(productSearchQuery);
        }
    }, [debouncedSearch]);

    useEffect(() => {
        if (searchLoading) {
            toggleDropdown(true);
            setShowLoading(true);
        }
        if (searchSuccess) {
            mapSearchResult(productSearchResult);
            setPrevSearchQuery(productSearchQuery);
            toggleDropdown(true);
            setShowLoading(false);
        }
    }, [searchLoading, searchSuccess]);

    const setProductSearchQueryAndCloseIfEmpty = (query: string) => {
        setProductSearchQuery(query);
        if (!query) toggleDropdown(false);
    }

    const searchOrOpenResults = () => {
        if (!productSearchQuery) return;

        if (productSearchQuery !== prevSearchQuery) {
            getProductSearchResult(productSearchQuery);
        } else if (!showDropdown) {
            toggleDropdown(true);
        }
    }

    const mapSearchResult = (searchResult: SearchProduct) => {
        const resultList = Object.keys(searchResult).map(k => {
            return reduceVariants(searchResult[k])
        })
        console.log('search: ', resultList);

        if (resultList) {
            setSearchResultList(resultList);
        }

    }

    const reduceVariants = (variants: SearchVariant[]) => {
        const resultItem = variants.reduce<SearchResult>((acc, curr) => {
            if ((!isOutOfStock(curr.stock) && !acc)) {
                acc = mapSearchResultData(curr, false)
            }

            return acc;
        }, undefined)

        return resultItem ?? mapSearchResultData(variants[0], true);
    }

    const isOutOfStock = (stock) => {
        return stock.quantityInStock === 0 && !stock.purchaseOrders.length
    }

    const mapSearchResultData = (variant: SearchVariant, outOfStock: boolean) => {
        return {
            baseId: variant.baseProductId,
            id: variant.variantId,
            img: variant.image,
            name: variant.name,
            outOfStock: outOfStock,
        }
    }

    const mapCartVariantData = (variant: SearchVariant): CartVariantData => {
        return {
            baseId: variant.baseProductId,
            id: variant.variantId,
            size: variant.size,
            color: variant.colorHex,
            colorName: variant.colorName,
            stock: {
                ...variant.stock,
                isOutOfStock: isOutOfStock(variant.stock)
            }
        };
    }

    const addToCartAndCloseResults = (id: string, baseId: string) => {
        const variantData = productSearchResult[baseId].map(k => {
            return mapCartVariantData(k);
        })

        addToCart(id, variantData)
        toggleDropdown(false);
    }

    const searchOrOpenResultsOnEnterKey = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            searchOrOpenResults();
        }
    };
  
  return(
    <div className="mb-6 px-6 pt-3.5 leading-4 border-t border-[#f8f5f0]">
        <span className="text-sm mb-1">
            {translations.customerServiceCartQuickSearch}
        </span>
        <div className="flex gap-1.5 relative">
            <input ref={impersonationSelectRef}
                autoFocus
                className="text-base relative h-10 border py-2 px-3.5 grow rounded-sm focus-within:border-[#015ECC] focus-within:outline-0"
                type="search"
                onChange={(e) => setProductSearchQueryAndCloseIfEmpty(e.currentTarget.value.trim())}
            />                     
            {(showDropdown) && 
                <div
                    className="absolute top-[100%] z-10 border rounded-sm bg-white w-[calc(100%-46px)] mt-1 text-sm max-h-[calc(80dvh-200px)] overflow-auto"
                    ref={impersonationDropdownRef as React.MutableRefObject<HTMLDivElement>}
                >
                    {searchSuccess && searchResultList.length > 0 && searchResultList.map((item) => (
                        <ProductSearchResult
                            key={item.id}
                            baseId={item.baseId}
                            id={item.id}
                            name={item.name}
                            img={item.img}
                            outOfStock={item.outOfStock}
                            addToCart={addToCartAndCloseResults}
                            outOfStockText={translations.customerServiceCartOutOfStock}
                        />
                    ))}
                    {searchSuccess && searchResultList.length === 0 && (
                        <div className="h-full w-full text-black py-2.5 px-2.5 border-b border-[#D5D3DE]" data-result="empty">
                            {translations.customerServiceCartQuickSearchEmpty}
                        </div>
                    )}
                    {searchError && (
                        <div className="h-full w-full text-black py-2.5 px-2.5 border-b border-[#D5D3DE]" data-result="error">
                            {translations.customerServiceCartQuickSearchError}
                        </div>
                    )}
                    {showLoading && (
                        <div className="h-full w-full text-black py-2.5 px-2.5 border-b border-[#D5D3DE]" data-loading="true">
                            <Loader size="10px" marginY="my-2" />
                        </div>
                    )}
                </div>
            }
            <button
                className="w-10 h-10 flex justify-center outline-[#015ECC] items-center text-white bg-black rounded-sm cursor-pointer"
                onClick={searchOrOpenResults}
                onKeyDown={searchOrOpenResultsOnEnterKey}
            >
                <MagnicifactionGlassIcon className="h-6 text-current"/>
            </button>
        </div>
    </div>
)};

export default ProductSearch;
